import { Button, DatePicker, Form, Modal, Select, Space, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
// import { ExclamationCircleOutlined } from '@ant-design/icons'
import { getThingModelExportData } from '@/api/data/history';
import { DeviceProperty, getHistoryProperty, getHistoryPropertyAll, HistoryProperty } from '@/api/device-thingmodel';
import { useTableRequest } from '@/hooks';
import { downloadXlsx } from '@/utils/download';
import { DownloadOutlined, LineChartOutlined, TableOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { isNull, isObject } from 'lodash';
import LineDraggableChart from './LineDraggableChart';

const { RangePicker } = DatePicker;
const { useForm } = Form;

// const range = (start: number, end: number) => {
//   const result = []
//   for (let i = start; i < end; i++) {
//     result.push(i)
//   }
//   return result
// }

export type EventValue<DateType> = DateType | null;
export type RangeValue<DateType> = [EventValue<DateType>, EventValue<DateType>] | null;

export interface Tab1ModalRef {
  open: (item: DeviceProperty) => void;
}

export interface Tab1ModalProps {
  product_id?: string;
}

export const Tab1Modal = forwardRef<Tab1ModalRef, Tab1ModalProps>((props, ref) => {
  const [searchParams] = useSearchParams();
  const params = useParams<{ id: string }>();
  const [form] = useForm();
  // const [dates, setDates] = useState<RangeValue<Dayjs>>()
  const [record, setRecord] = useState<DeviceProperty>();
  // const [dataSource, setDataSource] = useState<ReportDaum[]>([])
  // const [loading, setLoading] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const isModalOpenRef = useRef(isModalOpen);
  const recordRef = useRef(record);
  
  // 同步 ref 值
  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);
  
  useEffect(() => {
    recordRef.current = record;
  }, [record]);
  
  const { loading, dataSource, pagination, search } = useTableRequest<HistoryProperty>({
    onRequest: (param) => {
      // 只有当模态框打开且 record 存在时才执行请求
      if (!isModalOpenRef.current || !recordRef.current?.code) {
        return Promise.resolve({
          success: true,
          result: {
            list: [],
            total: 0,
          },
        } as any);
      }
      const [start, end] = form.getFieldValue('range') || [];
      const aggregateTime = form.getFieldValue('aggregateTime');
      const aggregateMethod = form.getFieldValue('aggregateMethod');
      return getHistoryProperty(params.id!, {
        ...param,
        code: recordRef.current?.code!,
        start: start?.valueOf()!,
        end: end?.valueOf()!,
        aggregateTime: aggregateTime && aggregateTime !== 'none' ? aggregateTime : undefined,
        aggregateMethod: aggregateTime && aggregateTime !== 'none' ? aggregateMethod : undefined,
      });
    },
  });
  const [type, setType] = useState<'line' | 'table'>('line');

  const { run: lineRun, data: lineData } = useRequest(
    (toParams?: { code?: string }) => {
      const [start, end] = form.getFieldValue('range') || [dayjs().add(-10, 'minutes'), dayjs()];
      const aggregateTime = form.getFieldValue('aggregateTime');
      const aggregateMethod = form.getFieldValue('aggregateMethod');
      console.log({ start, end, params, record });
      return getHistoryPropertyAll(params.id!, {
        code: toParams?.code || record?.code!,
        start: start?.valueOf()!,
        end: end?.valueOf()!,
        aggregateTime: aggregateTime && aggregateTime !== 'none' ? aggregateTime : undefined,
        aggregateMethod: aggregateTime && aggregateTime !== 'none' ? aggregateMethod : undefined,
      });
    },
    { manual: true },
  );

  // const { run: lineRun, data } = useRequest(getHistoryPropertyAll, { manual: true });
  const onSearch = ({ inType, code }: { inType?: 'line' | 'table'; code?: string }) => {
    if (inType ? inType === 'line' : type === 'line') {
      lineRun({ code });
      return;
    }
    search();
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        open(item) {
          setRecord(item);
          setIsModalOpen(true);
          onSearch({ code: item.code });
        },
      };
    },
    [params],
  );

  // const load = useCallback(() => {
  //   const [start, end] = form.getFieldValue('range') || []
  //   setLoading(true)
  //   getHistoryProperty(params.id!, {
  //     code: record?.code!,
  //     start: start?.valueOf()!,
  //     end: end?.valueOf()!,
  //   }).then(resp => {
  //     if (resp.success) {
  //       setDataSource(resp.result.list || [])
  //     }
  //   }).finally(() => {
  //     setLoading(false)
  //   })
  // }, [record?.code, form, params.id])

  // useEffect(() => {
  //   if (isModalOpen) {
  //     load()
  //   }
  // }, [isModalOpen])

  useEffect(() => {
    const start_at = Number(searchParams.get('start_at'));
    const end_at = Number(searchParams.get('end_at'));
    start_at && end_at && form.setFieldValue('range', [dayjs(start_at), dayjs(end_at)]);
  }, []);

  const { run: exportRun, loading: exportLoading } = useRequest(getThingModelExportData, {
    manual: true,
    onSuccess(data) {
      const fileName = window.decodeURI(data.headers['content-disposition']?.split('=')[1] || '');
      downloadXlsx(data.data, fileName);
    },
  });

  return (
    <Modal
      title="功能历史数据"
      open={isModalOpen}
      footer={null}
      width={1000}
      onCancel={() => {
        setIsModalOpen(false);
      }}
    >
      <Form layout="inline" className="mb5">
        <Form.Item label="功能名称">{record?.name}</Form.Item>
        <Form.Item label="标识符">{record?.code}</Form.Item>
        <Form.Item label="数据类型">{record?.data_type}</Form.Item>
      </Form>
      <div className="mb10 flex" style={{ justifyContent: 'space-between', gap: 16 }}>
        <Form layout="inline" form={form} style={{ flexWrap: 'nowrap', marginRight: 16 }}>
          <Form.Item name="range" initialValue={[dayjs().add(-10, 'minutes'), dayjs()]}>
            <RangePicker
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={['开始日期', '结束日期']}
              style={{ width: 320 }}
              // onCalendarChange={val => setDates(val)}
              // onOpenChange={(open) => {
              //   if (open) {
              //     form.setFieldValue('range', []);
              //   } else {
              //     // setDates(undefined)
              //   }
              // }}
              showTime={{
                hideDisabledOptions: true,
                defaultValue: [dayjs(dayjs().format('YYYY-MM-DD')), dayjs(dayjs().format('YYYY-MM-DD'))],
              }}
              presets={[
                { label: '最近10分钟', value: [dayjs().add(-10, 'minutes'), dayjs()] },
                { label: '最近20分钟', value: [dayjs().add(-20, 'minutes'), dayjs()] },
                { label: '最近30分钟', value: [dayjs().add(-30, 'minutes'), dayjs()] },
                { label: '最近1小时', value: [dayjs().add(-1, 'hours'), dayjs()] },
                { label: '最近3小时', value: [dayjs().add(-3, 'hours'), dayjs()] },
                { label: '最近5小时', value: [dayjs().add(-5, 'hours'), dayjs()] },
                { label: '最近7小时', value: [dayjs().add(-7, 'hours'), dayjs()] },
                { label: '最近12小时', value: [dayjs().add(-12, 'hours'), dayjs()] },
                { label: '最近24小时', value: [dayjs().add(-24, 'hours'), dayjs()] },
              ]}
              // renderExtraFooter={() => {
              //   return (
              //     <Text type="warning" style={{ fontSize: 12 }}>
              //       <ExclamationCircleOutlined className="ml20 mr10" />
              //       时间查询跨度应选择二十四小时之内，否则数据加载将消耗很长时间
              //     </Text>
              //   )
              // }}
            />
          </Form.Item>
          <Form.Item name="aggregateTime" initialValue="none" label="聚合时间">
            <Select
              style={{ width: 90 }}
              onChange={(value) => {
                if (value === 'none') {
                  form.setFieldValue('aggregateMethod', undefined);
                } else if (!form.getFieldValue('aggregateMethod')) {
                  form.setFieldValue('aggregateMethod', 'avg');
                }
              }}
            >
              <Select.Option value="none">不聚合</Select.Option>
              <Select.Option value="30s">30秒</Select.Option>
              <Select.Option value="1m">1分钟</Select.Option>
              <Select.Option value="2m">2分钟</Select.Option>
              <Select.Option value="5m">5分钟</Select.Option>
              <Select.Option value="10m">10分钟</Select.Option>
              <Select.Option value="30m">30分钟</Select.Option>
              <Select.Option value="1h">1小时</Select.Option>
              <Select.Option value="3h">3小时</Select.Option>
              <Select.Option value="6h">6小时</Select.Option>
              <Select.Option value="12h">12小时</Select.Option>
              <Select.Option value="1d">1天</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.aggregateTime !== currentValues.aggregateTime}>
            {() => {
              const aggregateTime = form.getFieldValue('aggregateTime');
              return aggregateTime && aggregateTime !== 'none' ? (
                <Form.Item name="aggregateMethod" initialValue="avg" label="聚合方法">
                  <Select style={{ width: 80 }}>
                    <Select.Option value="avg">平均数</Select.Option>
                    <Select.Option value="max">最大值</Select.Option>
                    <Select.Option value="min">最小值</Select.Option>
                    <Select.Option value="sum">求和</Select.Option>
                  </Select>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          <Form.Item style={{ marginRight: 0, whiteSpace: 'nowrap' }}>
            <Button
              type="primary"
              onClick={() => {
                onSearch({});
              }}
            >
              查询
            </Button>
            <Button
              className="ml10"
              onClick={() => {
                form.resetFields();
              }}
            >
              重置
            </Button>
          </Form.Item>
        </Form>
        <Space.Compact>
          <Button
            icon={<TableOutlined />}
            type={type === 'table' ? 'primary' : 'default'}
            onClick={() => {
              if (type === 'table') return;
              setType('table');
              onSearch({ inType: 'table' });
            }}
          />
          <Button
            type={type === 'line' ? 'primary' : 'default'}
            icon={<LineChartOutlined />}
            onClick={() => {
              if (type === 'line') return;
              setType('line');
              onSearch({ inType: 'line' });
            }}
          />
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            onClick={() => {
              const range = form.getFieldValue('range');
              exportRun({
                // @ts-ignore
                query: [
                  {
                    product_id: props?.product_id || '',
                    device_id: params.id!,
                    code: record?.code!,
                  },
                ],
                range: range?.map((v: Dayjs) => v.valueOf()),
                isAll: true,
              });
              // exportRun({ code: [record?.code!], range: range.map((v: Dayjs) => v.valueOf()), isAll: true, device_ids: [params.id!] });
            }}
          />
        </Space.Compact>
      </div>

      {type === 'line' && <LineDraggableChart data={(lineData?.result as any)?.list || []} height={400} range={form.getFieldValue('range')?.map((v: Dayjs) => v.valueOf())} />}
      {type === 'table' && (
        <Table
          size="small"
          scroll={{ y: 400 }}
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          rowKey="time"
          columns={[
            {
              title: '时间',
              key: 'time',
              dataIndex: 'time',
              width: 500,
              render(value) {
                return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
              },
            },
            {
              title: '原始值',
              key: 'value',
              dataIndex: 'value',
              render: (_v, item) => {
                // @ts-ignore
                return isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value);
              },
            },
          ]}
        ></Table>
      )}
      {/* <div style={{ margin: '0 auto', textAlign: 'center' }}>
        <Pagination {...pagination} style={{ textAlign: 'right', marginTop: 10, justifyContent: 'center' }} showSizeChanger showQuickJumper showTotal={(total) => `共 ${total} 条`}></Pagination>
      </div> */}
    </Modal>
  );
});

export default Tab1Modal;
